import { lazy } from "react";

// ---------------------------------------------------------------------------
// TEMPLATE REGISTRY — single source of truth for the Marketplace.
// Add a template:
//   1. Create the React site component in src/templates/sites/<Name>.jsx
//   2. Register it below (slug must match backend template_catalog.py for purchase)
// The Marketplace, filters, search, live-demo routes and cards are all generated
// from this array. No Marketplace code needs to change to add a template.
//
// `keywords` are extra searchable descriptors (style, tone, use-case) that never
// render on the card but power intuitive search (e.g. "modern", "responsive",
// "minimal", "dark"). They rank BELOW name/category/tag matches.
// ---------------------------------------------------------------------------

// NOTE: This registry holds ONLY the in-app React design prototypes (concept demos).
// Real, purchasable products come from the database (Template Factory) — never hardcode
// a DB id here. The Marketplace fetches /api/templates and resolves ids per-environment.
export const TEMPLATES = [
  {
    slug: "modern-business",
    name: "Momentum — Modern Business",
    category: "Business",
    description:
      "A bold, conversion-focused corporate site for agencies, startups, and consultancies.",
    previewImage:
      "https://images.unsplash.com/photo-1758518730083-4c12527b6742?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Corporate", "Startup", "Agency", "SaaS"],
    keywords: ["modern", "professional", "clean", "responsive", "minimal", "conversion", "bold", "light"],
    price: 89,
    customPrice: 1995,
    featured: true,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/ModernBusiness")),
  },
  {
    slug: "church-modern",
    name: "Cornerstone — Modern Church",
    category: "Churches",
    description:
      "A warm, welcoming church website with sermons, events, giving, and ministries.",
    previewImage:
      "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Ministry", "Faith", "Community", "Giving"],
    keywords: ["modern", "warm", "worship", "responsive", "welcoming", "sermons", "religious", "light"],
    price: 84,
    customPrice: 1895,
    featured: true,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/ChurchModern")),
  },
  {
    slug: "restaurant-modern",
    name: "Saveur — Fine Dining",
    category: "Restaurants",
    description:
      "An elegant restaurant experience with menu, reservations, gallery, and story.",
    previewImage:
      "https://images.unsplash.com/photo-1616671285410-2a676a9a433d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Dining", "Menu", "Reservations", "Hospitality"],
    keywords: ["modern", "elegant", "dark", "food", "responsive", "luxury", "restaurant", "cafe"],
    price: 79,
    customPrice: 1795,
    featured: false,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/RestaurantModern")),
  },
  {
    slug: "nonprofit",
    name: "BrightFuture — Nonprofit",
    category: "Nonprofit",
    description:
      "A heartfelt nonprofit site with impact stats, programs, donation CTAs, and stories.",
    previewImage:
      "https://images.unsplash.com/photo-1758599669199-a858720a9689?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Charity", "Donations", "Volunteer", "Impact"],
    keywords: ["modern", "warm", "charity", "responsive", "cause", "foundation", "community", "light"],
    price: 82,
    customPrice: 1795,
    featured: false,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/Nonprofit")),
  },
  {
    slug: "construction",
    name: "Ironclad — Construction",
    category: "Construction",
    description:
      "A bold, industrial construction site with services, projects, safety, and estimates.",
    previewImage:
      "https://images.unsplash.com/photo-1644221150186-5d785a471f44?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Contractor", "Commercial", "Industrial", "Building"],
    keywords: ["bold", "industrial", "dark", "responsive", "construction", "trades", "rugged", "strong"],
    price: 86,
    customPrice: 1895,
    featured: false,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/Construction")),
  },
  {
    slug: "coach-consultant",
    name: "Marcus Reid — Coach & Consultant",
    category: "Coaching",
    description:
      "A high-converting personal-brand site for coaches, speakers, and consultants.",
    previewImage:
      "https://images.unsplash.com/photo-1639747276707-a615a3a4655a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Coaching", "Speaking", "Personal Brand", "Programs"],
    keywords: ["modern", "bold", "personal brand", "responsive", "professional", "speaker", "consultant"],
    price: 84,
    customPrice: 1795,
    featured: true,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/CoachConsultant")),
  },
  {
    slug: "law-firm",
    name: "Whitmore & Hale — Law Firm",
    category: "Legal",
    description:
      "A distinguished law-firm site with practice areas, results, and free consultations.",
    previewImage:
      "https://images.unsplash.com/photo-1775144657610-9a6f171e522f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Attorney", "Litigation", "Corporate", "Family Law"],
    keywords: ["professional", "classic", "navy", "responsive", "elegant", "law", "lawyer", "trustworthy"],
    price: 92,
    customPrice: 2195,
    featured: true,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/LawFirm")),
  },
  {
    slug: "medical",
    name: "Vitalis — Medical Clinic",
    category: "Medical",
    description:
      "A clean, trustworthy clinic site with services, doctors, and online booking.",
    previewImage:
      "https://images.pexels.com/photos/32115905/pexels-photo-32115905.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tags: ["Healthcare", "Clinic", "Appointments", "Doctors"],
    keywords: ["modern", "clean", "light", "responsive", "healthcare", "trustworthy", "medical", "wellness"],
    price: 88,
    customPrice: 1995,
    featured: false,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/Medical")),
  },
  {
    slug: "portfolio",
    name: "Elena Voss — Creative Portfolio",
    category: "Portfolio",
    description:
      "A bold, dark portfolio for designers and creatives with a striking work grid.",
    previewImage:
      "https://images.unsplash.com/photo-1587522384446-64daf3e2689a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Creative", "Designer", "Showcase", "Freelance"],
    keywords: ["dark", "black", "bold", "minimal", "modern", "responsive", "creative", "portfolio"],
    price: 76,
    customPrice: 1695,
    featured: false,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/Portfolio")),
  },
  {
    slug: "author",
    name: "Eleanor Vance — Author",
    category: "Author",
    description:
      "A warm, literary author site with book showcase, bio, and press acclaim.",
    previewImage:
      "https://images.unsplash.com/photo-1598646506899-ac6be1000c2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tags: ["Books", "Writer", "Novels", "Literary"],
    keywords: ["warm", "literary", "classic", "minimal", "elegant", "responsive", "author", "writer"],
    price: 74,
    customPrice: 1595,
    featured: false,
    isNew: true,
    responsive: { desktop: true, tablet: true, mobile: true },
    component: lazy(() => import("./sites/Author")),
  },
];

export const getTemplates = () => TEMPLATES;

export const getTemplate = (slug) => TEMPLATES.find((t) => t.slug === slug) || null;

export const getCategories = () => {
  const cats = TEMPLATES.reduce((acc, t) => {
    acc[t.category] = (acc[t.category] || 0) + 1;
    return acc;
  }, {});
  return ["All", ...Object.keys(cats)];
};

// Relevance weights — higher = more important.
// Priority: exact name > name > category > tag > keyword > description.
const W = {
  nameExact: 120,
  nameStart: 80,
  nameWord: 60,
  name: 45,
  categoryExact: 55,
  category: 35,
  tagExact: 30,
  tag: 18,
  keyword: 12,
  description: 8,
};

const hasWord = (text, q) =>
  new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i").test(text);

// Score a single template against a normalized (lowercased, trimmed) query.
export function scoreTemplate(t, q) {
  if (!q) return 0;
  const name = (t.name || "").toLowerCase();
  const cat = (t.category || "").toLowerCase();
  const desc = (t.description || "").toLowerCase();
  const tags = (t.tags || []).map((x) => x.toLowerCase());
  const kws = (t.keywords || []).map((x) => x.toLowerCase());

  let s = 0;
  // Name
  if (name === q) s += W.nameExact;
  else if (name.startsWith(q)) s += W.nameStart;
  else if (hasWord(name, q)) s += W.nameWord;
  else if (name.includes(q)) s += W.name;
  // Category
  if (cat === q) s += W.categoryExact;
  else if (cat.includes(q)) s += W.category;
  // Tags
  if (tags.some((tg) => tg === q)) s += W.tagExact;
  else if (tags.some((tg) => tg.includes(q))) s += W.tag;
  // Keywords
  if (kws.some((k) => k.includes(q))) s += W.keyword;
  // Description
  if (desc.includes(q)) s += W.description;
  return s;
}

// Filter by category then rank matches by relevance (stable tie-break on registry order).
export function filterTemplates({ category = "All", query = "" } = {}) {
  const q = query.trim().toLowerCase();
  const inCat = TEMPLATES.filter((t) => category === "All" || t.category === category);
  if (!q) return inCat;
  return inCat
    .map((t, i) => ({ t, i, score: scoreTemplate(t, q) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.t);
}
